// Browser storage can throw (private mode, blocked site data). Every access goes
// through here so a failure degrades to "nothing remembered" instead of a crash.

type WriteListener = (key: string) => void
const listeners = new Set<WriteListener>()

/**
 * Follows every writeJSON/removeKey by key. The settings sync uses it to send
 * remembered preferences (table columns, report choices) to the server without
 * each page knowing about the API.
 */
export function onStorageWrite(listener: WriteListener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Every stored key starting with prefix; empty when storage is unavailable. */
export function keysWithPrefix(prefix: string, store: Storage = localStorage): string[] {
  try {
    return Object.keys(store).filter((key) => key.startsWith(prefix))
  } catch {
    return []
  }
}

export function readJSON<T>(key: string, fallback: T, store: Storage = localStorage): T {
  try {
    const value = store.getItem(key)
    return value === null ? fallback : (JSON.parse(value) as T)
  } catch {
    return fallback
  }
}

export function writeJSON(key: string, value: unknown, store: Storage = localStorage) {
  try {
    store.setItem(key, JSON.stringify(value))
  } catch {
    /* storage unavailable */
  }
  if (store === localStorage) listeners.forEach((listener) => listener(key))
}

export function removeKey(key: string, store: Storage = localStorage) {
  try {
    store.removeItem(key)
  } catch {
    /* storage unavailable */
  }
  if (store === localStorage) listeners.forEach((listener) => listener(key))
}
